import './hype.css';

/**
 * The thousand, celebrated over the top of whatever is on screen.
 *
 * ## Why it is deliberately small
 *
 * This lands on a broadcast somebody is watching, unannounced, several times a
 * night in a busy room. A screenful of confetti over a music video is a
 * platform interrupting its own content to congratulate itself. A ring, the
 * number, and a scatter of sparks reads at a glance and is gone in three
 * seconds, which is all a milestone is worth.
 *
 * ## Everybody sees it at the same moment
 *
 * There is no event for this. The room's total arrives once a second and every
 * client crosses the same thousand in the same message, so the celebration is
 * shared without anything having been sent to arrange it - and somebody who
 * arrives at 4,200 sees nothing, because `useHype` only fires on a crossing it
 * actually watched happen.
 */

/** Where the shards go, as fractions of the frame. Fixed, not random: a scatter
 *  that is different for every viewer is a scatter nobody can talk about. */
const SHARDS = [
  { x: -180, y: -120, spin: '-24deg', glyph: '🔥', delay: 0 },
  { x: 170, y: -140, spin: '18deg', glyph: '🔥', delay: 60 },
  { x: -240, y: 40, spin: '12deg', glyph: '✨', delay: 120 },
  { x: 230, y: 20, spin: '-16deg', glyph: '✨', delay: 40 },
  { x: -90, y: -210, spin: '30deg', glyph: '🤘', delay: 160 },
  { x: 110, y: -200, spin: '-28deg', glyph: '🙌', delay: 100 },
  { x: -300, y: -40, spin: '8deg', glyph: '🔥', delay: 200 },
  { x: 300, y: -60, spin: '-8deg', glyph: '🔥', delay: 140 },
] as const;

export function HypeBoom({ at }: { at: number }) {
  return (
    <div
      className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center overflow-hidden"
      data-hype-boom={at}
      aria-hidden
    >
      <div className="hype-ring absolute h-40 w-40 rounded-full border-2 border-white/70" />

      {SHARDS.map((shard) => (
        <span
          key={shard.glyph + shard.x}
          className="hype-shard absolute select-none text-3xl leading-none"
          style={{
            ['--x' as string]: `${shard.x}px`,
            ['--y' as string]: `${shard.y}px`,
            ['--spin' as string]: shard.spin,
            animationDelay: `${shard.delay}ms`,
          }}
        >
          {shard.glyph}
        </span>
      ))}

      {/*
        A soft dark halo under the type, because this lands on artwork.

        The number was drawn straight onto whatever was on the screen, and a
        white 9,000 over a sunset is a shape rather than a number. A radial
        wash reads as light behind the text rather than as a panel over the
        picture, which a rectangle would.
      */}
      <div className="hype-number relative flex flex-col items-center gap-1 px-16 py-10">
        <div
          className="absolute inset-0 bg-[radial-gradient(closest-side,rgb(0_0_0/0.62),transparent)]"
          aria-hidden
        />

        <span className="relative text-5xl font-semibold tabular-nums text-white drop-shadow-[0_4px_18px_rgb(0_0_0/0.75)]">
          {at.toLocaleString()}
        </span>
        <span className="relative font-mono text-[11px] uppercase tracking-[0.2em] text-white/85 drop-shadow-[0_2px_10px_rgb(0_0_0/0.9)]">
          the room is going off
        </span>
      </div>
    </div>
  );
}
